import { useMemo } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

/**
 * Select con búsqueda activa (autocompletar/filtrar mientras se escribe).
 *
 * Reutilizable en cualquier módulo. A diferencia de un <select> nativo, permite
 * escribir para filtrar las opciones en vivo (ej: escribir "prueba" y ver
 * "MEDICO DE PRUEBA 2"). La búsqueda ignora mayúsculas y tildes.
 *
 * Props:
 *  - options: [{ value, label, keywords? }]
 *      (keywords opcional para buscar por alias; también admite texto/array)
 *  - value: valor simple (string/number) o array de valores si isMulti.
 *      La comparación con las opciones es por texto, así 0 y '0' coinciden.
 *  - onChange: (valor, name) => void
 *      Para isMulti devuelve un array. Si `asEvent` es true, recibe un evento
 *      sintético { target: { id, name, value } } para reusar handlers tipo
 *      `const { name, value } = e.target`.
 *  - name: identificador que se devuelve junto al valor (id/name del evento).
 *  - asEvent: emite un evento sintético en vez del valor plano.
 *  - isMulti, isClearable, isDisabled, placeholder, noOptionsMessage
 *  - fontSize, height: ajustes visuales
 *  - ...rest: cualquier otra prop de react-select
 */
const normalizar = (txt) =>
    String(txt ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

export const SelectBuscable = ({
    options = [],
    value,
    onChange,
    name,
    isMulti = false,
    isClearable = true,
    isDisabled = false,
    closeOnMenuSelect = true,
    placeholder = 'Seleccione',
    noOptionsMessage = 'No hay resultados',
    fontSize = '13px',
    height = 40,
    className,
    asEvent = false,
    ...rest
}) => {

    // Convierte el valor simple en la opción que espera react-select.
    // Se compara por texto para tolerar tipos (0 vs '0').
    const selected = useMemo(() => {
        if (isMulti) {
            const values = (Array.isArray(value) ? value : []).map(String);
            return options.filter((o) => values.includes(String(o.value)));
        }
        return options.find((o) => String(o.value) === String(value)) || null;
    }, [options, value, isMulti]);

    const emitir = (valor) => {
        if (!onChange) return;
        if (asEvent) {
            onChange({ target: { id: name, name, value: valor } });
        } else {
            onChange(valor, name);
        }
    };

    const handleChange = (option) => {
        if (isMulti) {
            emitir((option || []).map((o) => o.value));
        } else {
            emitir(option ? option.value : '');
        }
    };

    // Búsqueda insensible a mayúsculas y tildes, sobre label, value y keywords
    const filterOption = (option, inputValue) => {
        const q = normalizar(inputValue);
        if (!q) return true;

        const coincide = (campo) => {
            if (campo === undefined || campo === null) return false;
            if (Array.isArray(campo)) return campo.some((c) => normalizar(c).includes(q));
            return normalizar(campo).includes(q);
        };

        const data = option.data || {};

        return (
            coincide(option.label) ||
            coincide(option.value) ||
            coincide(option.keywords) ||
            coincide(data.label) ||
            coincide(data.value) ||
            coincide(data.keywords)
        );
    };

    return (
        <Select
            options={options}
            value={selected}
            isMulti={isMulti}
            isSearchable
            isClearable={isClearable}
            isDisabled={isDisabled}
            closeMenuOnSelect={closeOnMenuSelect}
            onChange={handleChange}
            placeholder={placeholder}
            noOptionsMessage={() => noOptionsMessage}
            filterOption={filterOption}
            components={animatedComponents}
            className={className}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
                control: (base, state) => ({
                    ...base,
                    minHeight: height,
                    fontSize,
                    borderRadius: 10,
                    borderColor: state.isFocused ? 'var(--color-primario)' : '#cbd5e1',
                    boxShadow: state.isFocused ? '0 0 0 3px rgba(var(--color-primario-rgb),0.12)' : 'none',
                    '&:hover': { borderColor: 'var(--color-primario)' },
                }),
                valueContainer: (base) => ({ ...base, padding: '2px 10px' }),
                singleValue: (base) => ({ ...base, fontSize, color: '#334155' }),
                input: (base) => ({ ...base, fontSize }),
                placeholder: (base) => ({ ...base, fontSize, color: '#94a3b8' }),
                menu: (base) => ({ ...base, fontSize, borderRadius: 10, overflow: 'hidden' }),
                option: (base, state) => ({
                    ...base,
                    fontSize,
                    padding: '10px 14px',
                    backgroundColor: state.isSelected ? 'var(--color-primario)' : state.isFocused ? '#f1f5f9' : 'white',
                    color: state.isSelected ? 'white' : '#334155',
                    cursor: 'pointer',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            {...rest}
        />
    );
};
